import { useEffect, useState, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import Model3D from './Model3D';
import Plane from './Plane';
import _ from 'lodash';
import Camera from './Camera';
import FitCameraToModel from './FitCameraToModel';
import { defaultCanvasStyle, defaultCameraConfig } from './defaults';
import { CameraWrapper } from './CameraWrapper';

export default function Render3DScene(props) {
  const spotLight1Ref = useRef();
  const spotLight2Ref = useRef();
  const spotLight3Ref = useRef();
  const modelRef = useRef();
  const orbitControlsRef = useRef();

  const cameraConfig = { ...defaultCameraConfig, ...props.cameraConfig };
  const orbitConfig = { ...defaultCameraConfig.orbitControl, ...props.cameraConfig?.orbitControl };
  const canvasStyle = _.get(props, 'canvasStyle', defaultCanvasStyle);
  const planeConfig = _.get(props, 'options.planeConfig', {});
  const gridHelperConfig = _.get(props, 'options.gridHelper', null);
  const directionalLights = _.get(cameraConfig, 'lightSettings.directionalLights', []);
  const showAxes = _.get(props, 'options.axes', true);
  const fitToModel = _.get(props, 'options.fitToModel', false);
  const axesSize = _.get(cameraConfig, 'axesSize', 3);
  const axesPosition = _.get(cameraConfig, 'axesPosition', [0, 0, 0]);

  const [data, setData] = useState(null);
  const [cameraPosition, setCameraPosition] = useState(cameraConfig.position);

  useEffect(() => {
    setData(_.get(props, 'data', []));
  }, [props.data]);

  useEffect(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.addEventListener('change', () => {
        const camera = orbitControlsRef.current.object;
        setCameraPosition([camera.position.x, camera.position.y, camera.position.z]);
      });
    }
  }, [orbitControlsRef]);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Canvas
        style={canvasStyle}
        shadows
        camera={{ position: cameraPosition, fov: cameraConfig.fov }}
        gl={{ antialias: true }}
        onCreated={({ gl, scene }) => {
          gl.setClearColor(cameraConfig.background);
          gl.shadowMap.enabled = true;
          gl.shadowMap.type = THREE.PCFSoftShadowMap;
        }}>
        <CameraWrapper modelRef={modelRef} />
        <Camera position={cameraPosition} zoom={cameraConfig.zoom} fov={cameraConfig.fov} />
        {orbitConfig.enabled && (
          <OrbitControls ref={orbitControlsRef} maxDistance={orbitConfig.maxDistance} minDistance={orbitConfig.minDistance} maxPolarAngle={orbitConfig.maxPolarAngle} target={orbitConfig.target} />
        )}
        <ambientLight intensity={cameraConfig.lightSettings.ambientLight.intensity} />
        {directionalLights.map((light, index) => (
          <directionalLight key={`dir-${index}`} position={light.position} intensity={light.intensity} castShadow={light.castShadow} />
        ))}

        {_.get(props, 'options.spotLight', false) &&
          cameraConfig.lightSettings.spotLight.map((light, index) => (
            <spotLight
              key={index}
              ref={index === 0 ? spotLight1Ref : index === 1 ? spotLight2Ref : spotLight3Ref}
              position={light.position}
              angle={light.angle}
              intensity={light.intensity}
              penumbra={light.penumbra}
              castShadow={light.castShadow}
              decay={0}
            />
          ))}
        {showAxes && (
          <group position={axesPosition}>
            <axesHelper args={[axesSize]} />
          </group>
        )}
        <Model3D modelRef={modelRef} type={props.type} modelData={data} />
        {fitToModel && <FitCameraToModel modelRef={modelRef} controlsRef={orbitControlsRef} data={data} />}
        {gridHelperConfig && <gridHelper args={gridHelperConfig.args} position={gridHelperConfig.position} />}
        {_.get(props, 'options.plane', false) && <Plane {...planeConfig} />}
      </Canvas>
    </div>
  );
}
